import { useCallback, useEffect, useState } from "react";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { uploadExternalDocument } from "@services/uploadApi";
import { SasTokenPrefix } from "@services/user/types";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { UploadedDocument } from "../schema/vendorLicenseRequestFormSchema";

const isUploadedDocument = (entry: UploadEntry): entry is UploadedDocument => {
  if (entry instanceof File) return false;
  const e = entry as Record<string, unknown>;
  return typeof e.name === "string" && typeof e.url === "string" && e.url.length > 0;
};

export const useExternalDocumentUpload = <TForm extends FieldValues, K extends Path<TForm>>(
  field: K,
  prefix: SasTokenPrefix,
  formData?: UseFormReturn<TForm>,
  certificateType?: string,
  telemetryScreen = "VendorLicenseRequest",
  storeKey?: string,
) => {
  const { notify } = useNotification();
  const { t } = useTranslation();
  const fieldKey = String(field);
  const uploadStoreKey = storeKey ?? fieldKey;
  const isUploadInFlight = useUploadStore((state) => state.isUploadInFlight(uploadStoreKey));

  const [uploadEntries, setUploadEntriesState] = useState<UploadEntry[]>(() => {
    return useUploadStore.getState().getFilesForKey(uploadStoreKey) ?? [];
  });

  const hydrateFormValue = useCallback(
    (docs: UploadedDocument[]) => {
      if (!formData?.setValue) return;
      formData.setValue(field, docs as any, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    },
    [field, formData],
  );

  const setUploadEntries = useCallback(
    (nextEntries: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => {
      setUploadEntriesState((prev) => {
        const resolved = typeof nextEntries === "function" ? nextEntries(prev) : nextEntries;
        const docs = resolved.filter(isUploadedDocument);

        if (docs.length || !resolved.length) {
          hydrateFormValue(docs);
        }

        return resolved;
      });
    },
    [hydrateFormValue],
  );

  const setDocumentValue = useCallback(
    (docs: UploadedDocument[]) => {
      if (!formData?.setValue) return;
      const isSubmitted = formData.formState?.isSubmitted;

      formData.setValue(field, docs as any, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: Boolean(isSubmitted),
      });
    },
    [field, formData],
  );

  const clearDocumentValue = useCallback(() => {
    setDocumentValue([]);
    if (formData?.trigger && formData.formState?.isSubmitted) {
      void formData.trigger(field);
    }
  }, [field, formData, setDocumentValue]);

  useEffect(() => {
    const persisted = useUploadStore.getState().getFilesForKey(uploadStoreKey) ?? [];
    setUploadEntries(persisted);

    const docs = persisted.filter(isUploadedDocument);
    if (docs.length) hydrateFormValue(docs);
  }, [hydrateFormValue, uploadStoreKey]);

  const handleUploadError = useCallback(
    (error: unknown) => {
      console.error("[ExternalUpload] upload failed:", error);

      notify({
        title: t("licensing.notifications.vendor-license-request-upload-error-title"),
        description: t("licensing.notifications.vendor-license-request-upload-error-description"),
        type: "error",
      });

      useUploadStore.getState().clearFilesForKey(uploadStoreKey);
      setUploadEntries([]);
      clearDocumentValue();
    },
    [clearDocumentValue, notify, t, uploadStoreKey],
  );

  const handleRemove = useCallback(() => {
    useUploadStore.getState().clearFilesForKey(uploadStoreKey);
    setUploadEntries([]);
    clearDocumentValue();
  }, [clearDocumentValue, uploadStoreKey]);

  const uploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const res = await runTransaction(
        {
          module: "licensing",
          screen: telemetryScreen,
          action: "upload_license_document",
          entityType: "license_document",
        },
        (transactionId) =>
          uploadExternalDocument({
            file,
            prefix,
            certificateType: certificateType ?? String(field),
            ...(transactionId ? { transactionId } : {}),
          }),
      );

      return { documentUrl: res.documentUrl, file };
    },
    onMutate: () => {
      useUploadStore.getState().beginUploadInFlight(uploadStoreKey);
    },
    onSuccess: ({ documentUrl, file }) => {
      // if no documentUrl is returned, treat it as a failed upload and remove the file from the store
      if (!documentUrl) {
        handleRemove();
        return;
      }

      const entry: UploadedDocument = {
        name: file.name,
        url: documentUrl,
        type: file.type || "application/octet-stream",
      };

      setUploadEntries([entry]);
      useUploadStore.getState().setFilesForKey(uploadStoreKey, [entry]);
      setDocumentValue([entry]);
    },
    onError: handleUploadError,
    onSettled: () => {
      useUploadStore.getState().endUploadInFlight(uploadStoreKey);
    },
  });

  const handleUpload = useCallback(
    (files: File[]) => {
      const file = files?.[0];
      if (!file) return;

      useUploadStore.getState().clearFilesForKey(uploadStoreKey);
      setUploadEntries(files);
      clearDocumentValue();

      uploadMutation.mutate({ file });
    },
    [clearDocumentValue, uploadMutation, uploadStoreKey],
  );

  return {
    uploadStoreKey,
    uploadEntries,
    setUploadEntries,
    handleUpload,
    handleRemove,
    notSuccess: uploadMutation.isPending || uploadMutation.isIdle,
    isPending: uploadMutation.isPending || isUploadInFlight,
  };
};
